import React, { useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Row, Col } from "reactstrap";
import { Button } from "../../../Component";
import http from "../../../../utils/http-common";
import swal from "sweetalert";
import { isAbortError } from "../../../../utils/asyncRequestGuards";
import {
  linkPrimaryUserToPartner,
  contactIdentityPayloadFromUser,
  contactPersonEmailFromUser,
} from "../../../../utils/medicalPartnerPrimaryUser";
import { normalizeDialCodeDigits } from "../../../../utils/dialCodeFieldNormalize";
import { tcApprovalSelectValueFromPartner, tcApprovalApiStatusFromSelectValue } from "../medicalPartnerAdminConstants";
import { medicalPartnerPutConfig } from "../../../../utils/partnerEditLockRequest";
import CountryCodeSelect from "../../../common/CountryCodeSelect";
import EditMedicalPartnerProfileFormBody from "./EditMedicalPartnerProfileFormBody";

const emptyForm = () => ({
  organization_name: "",
  contact_person_country_code: "",
  contact_person_phone: "",
  contact_person_address: "",
  contact_person_city: "",
  contact_person_country: "",
  business_website: "",
  business_registration: "",
  offersMedicalTourism: true,
  remarks: "",
  defaultTimezone: "",
  defaultLanguage: "",
  tcApprovalNote: "",
});

async function resolvePartnerPrimaryUserObject(p, signal) {
  if (!p?.userId) return null;
  if (typeof p.userId === "object" && p.userId && p.userId._id) return p.userId;
  const raw = p.userId;
  const id = typeof raw === "object" && raw ? raw._id || raw : raw;
  if (!id) return null;
  try {
    const res = await http.get(`/api/v1/user/${String(id)}`, { signal });
    return res.data?.data || res.data || null;
  } catch (e) {
    if (isAbortError(e)) return null;
    return null;
  }
}

function initialPrimaryUserIdFromPartner(p) {
  if (!p?.userId) return "";
  if (typeof p.userId === "object" && p.userId && p.userId._id) return String(p.userId._id);
  return String(p.userId);
}

/**
 * Admin: partner dossier — Partner (record) vs Primary user (directory).
 * Props: isOpen, partnerId, onClose, onSaved(savedId), editLockToken, dossierEditBlocked
 */
const EditMedicalPartnerModal = ({
  isOpen,
  partnerId,
  onClose,
  onSaved,
  editLockToken = null,
  dossierEditBlocked = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedPrimaryUser, setSelectedPrimaryUser] = useState(null);
  const [initialPrimaryUserId, setInitialPrimaryUserId] = useState("");
  /** UI value for TC platform `<select>`: approved | pending | off (matches Overview). */
  const [tcSelect, setTcSelect] = useState("off");
  const [tcPlatformUpdatedAt, setTcPlatformUpdatedAt] = useState(null);
  /** Snapshot when modal loaded — used to avoid bumping tcApprovalUpdatedAt if unchanged. */
  const [tcSnapshot, setTcSnapshot] = useState({ select: "off", note: "" });
  const [activeTab, setActiveTab] = useState("partner");
  const [userDraft, setUserDraft] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    country_code: "",
    email: "",
  });

  useEffect(() => {
    if (!isOpen || !partnerId) return undefined;

    const ac = new AbortController();
    const { signal } = ac;
    setLoading(true);
    setForm(emptyForm());
    setSelectedPrimaryUser(null);
    setInitialPrimaryUserId("");
    setTcSelect("off");
    setTcPlatformUpdatedAt(null);
    setTcSnapshot({ select: "off", note: "" });
    setActiveTab("partner");
    setUserDraft({ first_name: "", last_name: "", phone: "", country_code: "", email: "" });

    (async () => {
      try {
        const res = await http.get(`/api/v1/medical-partners/${partnerId}`, { signal });
        const p = res.data?.data || res.data;
        if (!p) {
          swal("Error", "Partner not found", "error");
          setForm(emptyForm());
          onClose();
          return;
        }
        const u = await resolvePartnerPrimaryUserObject(p, signal);
        if (signal.aborted) return;
        const tcSel = tcApprovalSelectValueFromPartner(p);
        setTcSelect(tcSel);
        setTcPlatformUpdatedAt(p.tcApprovalUpdatedAt || null);
        setTcSnapshot({
          select: tcSel,
          note: String(p.tcApprovalNote || "").trim(),
        });
        setForm({
          organization_name: p.organization_name || "",
          contact_person_country_code: normalizeDialCodeDigits(p.contact_person_country_code) || "",
          contact_person_phone: p.contact_person_phone || "",
          contact_person_address: p.contact_person_address || "",
          contact_person_city: p.contact_person_city || "",
          contact_person_country: p.contact_person_country || "",
          business_website: p.business_website || "",
          business_registration: p.business_registration || "",
          offersMedicalTourism: p.offersMedicalTourism !== false,
          remarks: p.remarks || "",
          defaultTimezone: p.defaultTimezone || "",
          defaultLanguage: p.defaultLanguage || "",
          tcApprovalNote: p.tcApprovalNote || "",
        });
        setSelectedPrimaryUser(u);
        const initialUid = initialPrimaryUserIdFromPartner(p);
        setInitialPrimaryUserId(initialUid);
        if (u) {
          setUserDraft({
            first_name: u.first_name || "",
            last_name: u.last_name || "",
            phone: u.phone || "",
            country_code: normalizeDialCodeDigits(u.country_code) || "",
            email: u.email || "",
          });
        }
        if (initialUid && !u) {
          swal(
            "Primary user",
            "The linked user account could not be loaded. Select the primary user again before saving.",
            "warning"
          );
        }
      } catch (error) {
        if (isAbortError(error)) return;
        swal("Error", error.response?.data?.message || "Could not load partner", "error");
        setForm(emptyForm());
        onClose();
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [isOpen, partnerId, onClose]);

  useEffect(() => {
    if (!selectedPrimaryUser?._id) return;
    setUserDraft({
      first_name: selectedPrimaryUser.first_name || "",
      last_name: selectedPrimaryUser.last_name || "",
      phone: selectedPrimaryUser.phone || "",
      country_code: normalizeDialCodeDigits(selectedPrimaryUser.country_code) || "",
      email: selectedPrimaryUser.email || "",
    });
  }, [
    selectedPrimaryUser?._id,
    selectedPrimaryUser?.first_name,
    selectedPrimaryUser?.last_name,
    selectedPrimaryUser?.phone,
    selectedPrimaryUser?.country_code,
    selectedPrimaryUser?.email,
  ]);

  const primaryUserFilterPartner = useMemo(() => ({ _id: partnerId }), [partnerId]);

  const handleClose = () => {
    if (saving) return;
    setForm(emptyForm());
    setSelectedPrimaryUser(null);
    setInitialPrimaryUserId("");
    setActiveTab("partner");
    onClose();
  };

  const savePartner = async () => {
    if (!partnerId) return;
    if (dossierEditBlocked) {
      swal("Read-only", "Another admin holds the edit lock, or the session expired.", "info");
      return;
    }
    const org = form.organization_name.trim();
    if (!org) {
      swal("Validation", "Organization name is required.", "warning");
      return;
    }
    if (!selectedPrimaryUser?._id) {
      swal("Validation", "Select a primary user from the directory.", "warning");
      return;
    }
    const nextId = String(selectedPrimaryUser._id);
    const prevId = initialPrimaryUserId;

    if (prevId && prevId !== nextId) {
      const ok = await swal({
        title: "Change primary account?",
        text: "The previous primary user may lose partner ownership on their profile unless they remain a co-manager.",
        icon: "warning",
        buttons: ["Cancel", "Change primary"],
        dangerMode: true,
      });
      if (!ok) return;
    }

    setSaving(true);
    try {
      if (!prevId || prevId !== nextId) {
        const linkPayload = { userId: nextId };
        await linkPrimaryUserToPartner(
          partnerId,
          nextId,
          medicalPartnerPutConfig(editLockToken, linkPayload)
        );
      }

      let userForIdentity = selectedPrimaryUser;
      try {
        const ur = await http.get(`/api/v1/user/${nextId}`);
        userForIdentity = ur.data?.data || ur.data || selectedPrimaryUser;
      } catch {
        /* keep picker snapshot */
      }
      const email = contactPersonEmailFromUser(userForIdentity);
      if (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
        swal("Validation", "The selected user must have a valid email on their account.", "warning");
        setSaving(false);
        return;
      }
      const identity = contactIdentityPayloadFromUser(userForIdentity);
      const noteNow = String(form.tcApprovalNote || "").trim();
      const tcDirty = tcSelect !== tcSnapshot.select || noteNow !== tcSnapshot.note;

      const partnerPayload = {
        userId: nextId,
        organization_name: org,
        ...identity,
        contact_person_country_code: normalizeDialCodeDigits(form.contact_person_country_code) || null,
        contact_person_phone: form.contact_person_phone.trim() || null,
        contact_person_address: form.contact_person_address.trim() || null,
        contact_person_city: form.contact_person_city.trim() || null,
        contact_person_country: form.contact_person_country.trim() || null,
        business_website: form.business_website.trim() || null,
        business_registration: form.business_registration.trim() || null,
        offersMedicalTourism: Boolean(form.offersMedicalTourism),
        remarks: form.remarks.trim() || null,
        defaultTimezone: form.defaultTimezone.trim() || null,
        defaultLanguage: form.defaultLanguage.trim() || null,
        ...(tcDirty
          ? {
              tcApprovalStatus: tcApprovalApiStatusFromSelectValue(tcSelect),
              tcApprovalNote: noteNow || null,
              tcApprovalUpdatedAt: new Date().toISOString(),
            }
          : {}),
      };
      await http.put(
        `/api/v1/medical-partners/${partnerId}`,
        partnerPayload,
        medicalPartnerPutConfig(editLockToken, partnerPayload)
      );
      if (onSaved) await onSaved(String(partnerId));
      swal("Success", "Partner profile updated", "success");
      setForm(emptyForm());
      setSelectedPrimaryUser(null);
      setInitialPrimaryUserId("");
      onClose();
    } catch (error) {
      swal("Error", error.response?.data?.message || "Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveUser = async () => {
    if (!partnerId) return;
    if (dossierEditBlocked) {
      swal("Read-only", "Another admin holds the edit lock, or the session expired.", "info");
      return;
    }
    const uid = selectedPrimaryUser?._id ? String(selectedPrimaryUser._id) : "";
    if (!uid) {
      swal("Validation", "Select a primary user on the Partner tab first.", "warning");
      return;
    }
    setSaving(true);
    try {
      await http.put(`/api/v1/user/${uid}`, {
        first_name: userDraft.first_name.trim() || null,
        last_name: userDraft.last_name.trim() || null,
        phone: userDraft.phone.trim() || null,
        country_code: normalizeDialCodeDigits(userDraft.country_code) || null,
      });
      const ur = await http.get(`/api/v1/user/${uid}`);
      const fresh = ur.data?.data || ur.data;
      if (fresh) setSelectedPrimaryUser(fresh);
      if (onSaved) await onSaved(String(partnerId));
      swal("Success", "Primary user updated", "success");
    } catch (error) {
      swal("Error", error.response?.data?.message || "Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const onSave = () => {
    if (activeTab === "user") saveUser();
    else savePartner();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      className="modal-dialog-centered"
      size="lg"
      backdrop={saving ? "static" : true}
      style={{ zIndex: 2000 }}
    >
      <ModalHeader toggle={handleClose}>Partner profile</ModalHeader>
      <ModalBody className="modal-body-md">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-soft small mt-2 mb-0">Loading partner…</p>
          </div>
        ) : (
          <>
            {dossierEditBlocked ? (
              <div className="alert alert-warning small mb-3" role="status">
                This dossier is read-only until you take over the edit lock from the partner page banner.
              </div>
            ) : null}
            <ul className="nav nav-tabs nav-tabs-card mb-3" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  type="button"
                  className={`nav-link${activeTab === "partner" ? " active" : ""}`}
                  role="tab"
                  aria-selected={activeTab === "partner"}
                  onClick={() => setActiveTab("partner")}
                >
                  Partner
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  type="button"
                  className={`nav-link${activeTab === "user" ? " active" : ""}`}
                  role="tab"
                  aria-selected={activeTab === "user"}
                  onClick={() => setActiveTab("user")}
                >
                  Primary user
                </button>
              </li>
            </ul>

            {activeTab === "partner" ? (
              <EditMedicalPartnerProfileFormBody
                form={form}
                setForm={setForm}
                partnerId={partnerId}
                primaryUserFilterPartner={primaryUserFilterPartner}
                selectedPrimaryUser={selectedPrimaryUser}
                onPrimaryUserChange={setSelectedPrimaryUser}
                formDisabled={saving || dossierEditBlocked}
                tcSelect={tcSelect}
                onTcSelectChange={setTcSelect}
                teleconsultationPlatformUpdatedAt={tcPlatformUpdatedAt}
              />
            ) : (
              <Row className="g-3 gy-3">
                <Col md="12">
                  <p className="text-soft small mb-0">
                    Directory account used for login and notifications. Email changes sync to the partner record when you save the Partner tab.
                  </p>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label className="form-label">Email</Label>
                    <input type="email" className="form-control" value={userDraft.email} readOnly disabled />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label className="form-label">First name</Label>
                    <input
                      type="text"
                      className="form-control"
                      value={userDraft.first_name}
                      onChange={(e) => setUserDraft((s) => ({ ...s, first_name: e.target.value }))}
                      disabled={saving || dossierEditBlocked}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label className="form-label">Last name</Label>
                    <input
                      type="text"
                      className="form-control"
                      value={userDraft.last_name}
                      onChange={(e) => setUserDraft((s) => ({ ...s, last_name: e.target.value }))}
                      disabled={saving || dossierEditBlocked}
                    />
                  </FormGroup>
                </Col>
                <Col md="7">
                  <FormGroup>
                    <Label className="form-label" htmlFor={`edit-primary-user-dial-${partnerId || "p"}`}>
                      Country calling code
                    </Label>
                    <CountryCodeSelect
                      id={`edit-primary-user-dial-${partnerId || "p"}`}
                      value={userDraft.country_code}
                      onChange={(digits) => setUserDraft((s) => ({ ...s, country_code: digits || "" }))}
                      disabled={saving || dossierEditBlocked}
                    />
                  </FormGroup>
                </Col>
                <Col md="5">
                  <FormGroup>
                    <Label className="form-label">Phone</Label>
                    <input
                      type="text"
                      className="form-control"
                      value={userDraft.phone}
                      onChange={(e) => setUserDraft((s) => ({ ...s, phone: e.target.value }))}
                      disabled={saving || dossierEditBlocked}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-end flex-wrap gap-2">
        <div className="d-flex flex-wrap gap-2">
          <Button color="light" type="button" disabled={saving || loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button color="primary" type="button" disabled={saving || loading || dossierEditBlocked} onClick={onSave}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Saving…
              </>
            ) : activeTab === "user" ? (
              "Save primary user"
            ) : (
              "Save partner"
            )}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default EditMedicalPartnerModal;
